import { RSAA } from 'redux-api-middleware'
import { getApiPath, makeQueryParams } from 'sidecar-utility-library'

import { initialState, types } from 'store/Applicant'
import { siftApplicants, cleanTempApplicant } from 'utils/applicants'

/**
 * Toggles a medical history answer for an applicant by either adding the answer
 * object to their medical history answers array or removing the answer object
 * from the array. The answer must always be provided so that we can remove the
 * answer even when the value is false.
 *
 * @param {str} uuid The UUID of the applicant to toggle for
 * @param {obj} answer The answer that we want to toggle
 * @param {boo} toggleOn Determines whether to add or remove an answer
 */
export const toggleMedicalHistory = (uuid, answer, toggleOn) => ({
  type: types.TOGGLE_MEDICAL_HISTORY,
  payload: { uuid, answer, toggleOn },
})

// this validate endpoint is public and used for the getQuote flow. It doesn't
// require any uuid from webStorage nor a token, just the x-api-key.
export const validateMedicalHistory = (applicant, funnelType) => ({
  [RSAA]: {
    endpoint: getApiPath(
      'acct',
      `pub/application/medicalHistory/validate${makeQueryParams({
        funnelType,
      })}`,
    ),
    headers: {
      'x-api-key': process.env.REACT_APP_X_API_KEY,
      token: 'IGNORE',
    },
    method: 'POST',
    body: JSON.stringify({
      funnelType,
      applicant: cleanTempApplicant(applicant, { raiseStartDate: true }),
    }),
    types: [
      types.VALIDATE_MEDICAL_HISTORY_REQUEST,
      {
        type: types.VALIDATE_MEDICAL_HISTORY_SUCCESS,
        meta: applicant,
      },
      {
        type: types.VALIDATE_MEDICAL_HISTORY_ERROR,
        meta: applicant,
      },
    ],
  },
})

export const fetchMedicalHistory = uuid => ({
  [RSAA]: {
    endpoint: getApiPath(`acct`, `members/${uuid}/medicalHistory`),
    method: 'GET',
    types: [
      types.FETCH_MEDICAL_HISTORY_REQUEST,
      {
        type: types.FETCH_MEDICAL_HISTORY_SUCCESS,
        meta: { uuid },
      },
      types.FETCH_MEDICAL_HISTORY_ERROR,
    ],
  },
})

export default (
  state = initialState,
  { type, payload = {}, meta = {} } = {},
) => {
  switch (type) {
    case types.TOGGLE_MEDICAL_HISTORY: {
      state = { ...state, applicantDenied: '' }

      const { uuid, answer, toggleOn } = payload
      const currentAnswers =
        state.applicants.entities[uuid]?.medicalHistory?.answers || []

      const updatedAnswers = toggleOn
        ? [...currentAnswers, answer]
        : currentAnswers.filter(h => h.uuid !== answer.uuid)

      const currentMedicalHistory = state.applicants.entities[uuid]
        ?.medicalHistory || { answers: [] }

      const nextState = {
        ...state,
        applicants: {
          ...state.applicants,
          entities: {
            ...state.applicants.entities,
            [uuid]: {
              ...state.applicants.entities[uuid],
              medicalHistory: {
                ...currentMedicalHistory,
                answers: updatedAnswers,
              },
            },
          },
        },
      }

      return siftApplicants(nextState)
    }

    case types.VALIDATE_MEDICAL_HISTORY_REQUEST:
    case types.FETCH_MEDICAL_HISTORY_REQUEST: {
      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: true,
          error: '',
        },
      }
    }
    // this returns a 204 No Content, so there's nothing really to do.
    case types.VALIDATE_MEDICAL_HISTORY_SUCCESS: {
      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
        },
      }
    }

    case types.VALIDATE_MEDICAL_HISTORY_ERROR: {
      // 412 is returned only when member is denied
      // so if we have that, just toggle the flag
      // but don't store the error. We don't want to
      // show the error AND the deny modal.
      const isDenyStatus = payload.status === 412
      const responseMsg = payload.response?.message || ''
      const isDenyMessage = /den(y|ied)/.test(responseMsg)
      const message =
        'Sorry, but we are currently experiencing an issue on our side. Please try again.'

      state =
        isDenyStatus || isDenyMessage
          ? { ...state, applicantDenied: meta.uuid }
          : {
              ...state,
              applicants: {
                ...state.applicants,
                error: message,
              },
            }

      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
        },
      }
    }

    case types.FETCH_MEDICAL_HISTORY_SUCCESS: {
      const nextState = {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          entities: {
            ...state.applicants.entities,
            [meta.uuid]: {
              ...state.applicants.entities[meta.uuid],
              medicalHistory: payload.medicalHistory,
            },
          },
        },
      }
      return siftApplicants(nextState)
    }

    case types.FETCH_MEDICAL_HISTORY_ERROR: {
      return {
        ...state,
        applicants: {
          ...state.applicants,
          busy: false,
          error: payload.response.message,
        },
      }
    }

    default:
      return state
  }
}
